/**
 * Fraud Detection API Client
 * 
 * Phase 9.0: Fraud detection dashboard and player management
 */

import { adminApiRequest } from "./admin-api-client";

// ============================================================================
// Types
// ============================================================================

export interface FraudFlag {
  id: string;
  shopId: string;
  playerId: string | null;
  type: string;
  severity: number;
  meta: any;
  createdAt: string;
  player?: {
    id: string;
    fingerprint: string;
    customerId: string | null;
    isBlocked: boolean;
  };
}

export interface FraudStats {
  totalFlags: number;
  flagsByType: { type: string; count: number }[];
  flagsBySeverity: { severity: number; count: number }[];
  recentFlags: FraudFlag[];
  blockedPlayers: number;
  flagsLast24h: number;
  flagsLast7d: number;
}

export interface FraudFlagsFilter {
  type?: string;
  severity?: number;
  playerId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface BlockedPlayer {
  id: string;
  fingerprint: string;
  customerId: string | null;
  isBlocked: boolean;
  createdAt: string;
  _count: {
    sessions: number;
    fraudFlags: number;
  };
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get fraud detection statistics
 */
export async function getFraudStats(): Promise<FraudStats> {
  return adminApiRequest<FraudStats>("/admin/fraud/stats");
}

/**
 * Get fraud flags with filters
 */
export async function getFraudFlags(filter: FraudFlagsFilter = {}): Promise<{ flags: FraudFlag[]; total: number }> {
  const params = new URLSearchParams();
  if (filter.type) params.append("type", filter.type);
  if (filter.severity !== undefined) params.append("severity", filter.severity.toString());
  if (filter.playerId) params.append("playerId", filter.playerId);
  if (filter.dateFrom) params.append("dateFrom", filter.dateFrom);
  if (filter.dateTo) params.append("dateTo", filter.dateTo);
  if (filter.limit) params.append("limit", filter.limit.toString());
  if (filter.offset) params.append("offset", filter.offset.toString());

  const query = params.toString();
  return adminApiRequest<{ flags: FraudFlag[]; total: number }>(
    `/admin/fraud/flags${query ? `?${query}` : ""}`
  );
}

/**
 * Get fraud history for a specific player
 */
export async function getPlayerFraudHistory(playerId: string): Promise<{ flags: FraudFlag[] }> {
  return adminApiRequest<{ flags: FraudFlag[] }>(`/admin/fraud/players/${playerId}`);
}

/**
 * Block a player
 */
export async function blockPlayer(playerId: string, reason?: string): Promise<{ success: boolean; message: string }> {
  return adminApiRequest<{ success: boolean; message: string }>(
    `/admin/fraud/players/${playerId}/block`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    }
  );
}

/**
 * Unblock a player
 */
export async function unblockPlayer(playerId: string): Promise<{ success: boolean; message: string }> {
  return adminApiRequest<{ success: boolean; message: string }>(
    `/admin/fraud/players/${playerId}/unblock`,
    {
      method: "POST",
    }
  );
}

/**
 * Get list of blocked players
 */
export async function getBlockedPlayers(): Promise<{ players: BlockedPlayer[] }> {
  return adminApiRequest<{ players: BlockedPlayer[] }>("/admin/fraud/blocked-players");
}
